import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGES, STAGE_MAP, HEALTH_STYLE } from "@/lib/constants";
import { DESIGNATION_LABELS } from "@/pages/Team";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Lock, CheckCircle2, ShieldCheck, RefreshCw, Archive,
  ThumbsUp, RotateCcw, XCircle, Clock, MessageSquarePlus, UserCheck, Rocket,
} from "lucide-react";

export default function IdeaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [idea, setIdea] = useState(null);
  const [team, setTeam] = useState([]);
  const [stageOwners, setStageOwners] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/ideas/${id}`),
      api.get("/team"),
      api.get("/settings/stage-owners"),
    ]).then(([i, t, o]) => { setIdea(i.data); setTeam(t.data); setStageOwners(o.data); })
      .catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!idea) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const stage = STAGE_MAP[idea.current_stage];
  const gate = idea.gates?.[idea.current_stage] || {};
  const stageIndex = idea.stage_index;
  const effectiveOwnerId = (idea.assignments || {})[idea.current_stage] || stageOwners[idea.current_stage];
  const currentOwner = team.find((m) => m.id === effectiveOwnerId) || null;
  const allGreen = (gate.checklist || []).every((c) => c.checked);
  const scorecardOk = idea.current_stage !== "production_pod" || (idea.ai?.scorecard?.overall === 100);
  const canSign = !!currentOwner && allGreen && scorecardOk;
  const health = HEALTH_STYLE[idea.health] || HEALTH_STYLE.idle;

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      setIdea(r.data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleItem = (itemId, checked) =>
    act(() => api.post(`/ideas/${id}/checklist`, { stage: idea.current_stage, item_id: itemId, checked }));
  const rerunAI = () => act(() => api.post(`/ideas/${id}/ai/rerun`), "AI agent re-run");
  const signGate = () => act(() => api.post(`/ideas/${id}/signoff`, { stage: idea.current_stage }), "Gate signed — advanced to next stage");
  const assign = (memberId) => act(() => api.post(`/ideas/${id}/assign`, { stage: idea.current_stage, member_id: memberId || null }), "Responsible party updated");
  const toggleReadiness = (itemId, checked) => act(() => api.post(`/ideas/${id}/readiness`, { item_id: itemId, checked }));
  const isHumanStage = ["lab_incubation", "ai_council_review", "production_pod", "adoption_scale"].includes(idea.current_stage);

  return (
    <div className="p-6 max-w-6xl">
      <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors duration-200 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to pipeline
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StatusBadge status={idea.status} />
            <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${health.cls}`}>{health.label}</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono"><Clock className="w-3 h-3" />{idea.stage_age_days}d in stage</span>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">{idea.title}</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{idea.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {idea.tags?.map((t) => (
              <span key={t} className="font-mono text-[10px] px-2 py-0.5 bg-secondary rounded-sm text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>
        {idea.status === "active" && (
          <Button data-testid="park-btn" variant="outline" onClick={() => act(() => api.post(`/ideas/${id}/park`, { reason: "Manually parked" }), "Idea parked")} className="rounded-sm border-border">
            <Archive className="w-4 h-4 mr-1.5" /> Park
          </Button>
        )}
      </div>

      <Stepper current={stageIndex} status={idea.status} />

      {idea.status === "parked" || idea.status === "rejected" ? (
        <div className="mt-6 border border-warning/40 bg-warning/10 rounded-sm p-4">
          <p className="font-semibold text-warning text-sm capitalize">{idea.status}</p>
          <p className="text-sm text-muted-foreground mt-1">{idea.park_reason}</p>
          <Button data-testid="revive-btn" onClick={() => act(() => api.post(`/ideas/${id}/revive`), "Idea revived")} className="rounded-sm mt-3" size="sm">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Revive to pipeline
          </Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4 mt-6">
          <div className="lg:col-span-2 space-y-4">
            {isHumanStage && (
              <AssignmentBar team={team} currentOwner={currentOwner} isDefault={!(idea.assignments || {})[idea.current_stage]} onAssign={assign} busy={busy} stage={stage} />
            )}
            <AISection idea={idea} onRerun={rerunAI} busy={busy} reload={setIdea} ideaId={id} act={act} />

            {idea.current_stage === "ai_council_review" ? (
              <CouncilPanel idea={idea} currentOwner={currentOwner} act={act} ideaId={id} busy={busy} />
            ) : idea.current_stage === "adoption_scale" ? (
              <AdoptionPanel idea={idea} act={act} ideaId={id} busy={busy} currentOwner={currentOwner} canSign={canSign} allGreen={allGreen} signGate={signGate} gate={gate} toggleItem={toggleItem} />
            ) : isHumanStage ? (
              <GatePanel gate={gate} stage={stage} currentOwner={currentOwner} canSign={canSign} allGreen={allGreen}
                busy={busy} signGate={signGate} toggleItem={toggleItem} idea={idea} />
            ) : (
              <AutoGateNote gate={gate} stage={stage} />
            )}
          </div>

          <div className="space-y-4">
            <DemoReadinessPanel idea={idea} onToggle={toggleReadiness} busy={busy} />
            <HistoryPanel idea={idea} />
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "text-primary border-primary/40 bg-primary/10",
    parked: "text-warning border-warning/40 bg-warning/10",
    rejected: "text-danger border-danger/40 bg-danger/10",
    completed: "text-success border-success/40 bg-success/10",
  };
  const label = status === "completed" ? "Product Ready" : status;
  return <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm capitalize ${map[status]}`}>{label}</span>;
}

function Stepper({ current, status }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {STAGES.map((s, i) => {
        const done = i < current || status === "completed";
        const active = i === current && status === "active";
        return (
          <div key={s.key} className="flex items-center gap-1 shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs ${
              active ? "border-primary bg-primary/10 text-white"
              : done ? "border-success/40 bg-success/10 text-success"
              : "border-border text-muted-foreground"}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="font-mono">{i + 1}</span>}
              <span className="font-semibold whitespace-nowrap">{s.short}</span>
            </div>
            {i < STAGES.length - 1 && <div className={`w-4 h-px ${done ? "bg-success/40" : "bg-border"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function AISection({ idea, onRerun, busy, ideaId, act }) {
  const stage = idea.current_stage;
  const ai = idea.ai || {};
  let body = null;

  if (stage === "idea_initiation" && ai.relevance) {
    const r = ai.relevance;
    body = (
      <>
        <Metric label="Relevance Score" value={r.relevance_score} suffix="/100" />
        <p className="text-sm text-muted-foreground mt-2">{r.reason}</p>
        <p className="text-xs text-muted-foreground mt-2"><b className="text-white">Dedupe:</b> {r.dedupe_note}</p>
      </>
    );
  } else if (stage === "vector_validation" && ai.feasibility) {
    const f = ai.feasibility;
    body = (
      <>
        <div className="grid grid-cols-3 gap-2">
          <Metric label="Feasibility" value={f.feasibility_score} small />
          <Metric label="Impact" value={f.impact_score} small />
          <Metric label="Alignment" value={f.alignment_score} small />
        </div>
        <p className="text-sm text-muted-foreground mt-3">{f.summary}</p>
        {f.risks?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {f.risks.map((r, i) => <li key={i} className="text-xs text-warning">⚠ {r}</li>)}
          </ul>
        )}
      </>
    );
  } else if (stage === "production_pod") {
    return <ScorecardPanel idea={idea} ideaId={ideaId} act={act} busy={busy} />;
  } else if (stage === "lab_incubation") {
    body = <p className="text-sm text-muted-foreground">POC Advisor tracks technical & business gaps. Complete the checklist below and attach validation evidence before the Pod Lead signs the gate.</p>;
  } else {
    body = <p className="text-sm text-muted-foreground">No AI output for this stage yet.</p>;
  }

  return (
    <div className="border border-ai/30 bg-card rounded-sm p-4 ai-glow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-ai" />
          <h3 className="font-heading font-bold tracking-tight">AI Agent — {STAGE_MAP[stage].ai}</h3>
        </div>
        <Button data-testid="rerun-ai-btn" size="sm" variant="outline" onClick={onRerun} disabled={busy} className="rounded-sm border-border h-8">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Re-run</span>
        </Button>
      </div>
      {body}
      <p className="text-[10px] text-muted-foreground font-mono mt-3 pt-3 border-t border-border">
        AI RECOMMENDS — the named owner makes the final call.
      </p>
    </div>
  );
}

function ScorecardPanel({ idea, ideaId, act, busy }) {
  const sc = idea.ai?.scorecard;
  const [edits, setEdits] = useState({});
  if (!sc) return null;
  const items = sc.items.map((it) => ({ ...it, score: edits[it.id] ?? it.score }));
  const overall = Math.round(items.reduce((a, b) => a + b.score, 0) / items.length);

  const save = () => act(() => api.post(`/ideas/${ideaId}/scorecard`, {
    scores: items.map((i) => ({ id: i.id, score: i.score })),
  }), "Scorecard updated");
  const rerun = () => act(() => api.post(`/ideas/${ideaId}/scorecard/rerun`), "AI regenerated scorecard");

  return (
    <div className="border border-ai/30 bg-card rounded-sm p-4 ai-glow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-ai" />
          <h3 className="font-heading font-bold tracking-tight">Enterprise-Readiness Scorecard</h3>
        </div>
        <Button size="sm" variant="outline" onClick={rerun} disabled={busy} className="rounded-sm border-border h-8" data-testid="scorecard-rerun-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-run AI
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className={`font-mono text-3xl font-bold ${overall === 100 ? "text-success" : "text-warning"}`}>{overall}%</span>
        <div className="flex-1">
          <Progress value={overall} className="h-2" />
          <p className="text-[10px] font-mono text-muted-foreground mt-1">Must hit 100% to unlock SPE + PM sign-off.</p>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} data-testid={`scorecard-item-${it.id}`} className="border border-border rounded-sm p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">{it.category}</span>
              <span className={`font-mono text-sm ${it.score === 100 ? "text-success" : "text-warning"}`}>{it.score}%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">{it.note}</p>
            <input type="range" min="0" max="100" value={it.score}
              onChange={(e) => setEdits({ ...edits, [it.id]: parseInt(e.target.value) })}
              data-testid={`scorecard-slider-${it.id}`}
              className="w-full accent-[#8B5CF6]" />
          </div>
        ))}
      </div>
      <Button data-testid="scorecard-save-btn" onClick={save} disabled={busy} size="sm" className="rounded-sm mt-3 w-full">Save scores</Button>
    </div>
  );
}

function GatePanel({ gate, stage, currentOwner, canSign, allGreen, busy, signGate, toggleItem, idea }) {
  const scOk = idea.current_stage !== "production_pod" || (idea.ai?.scorecard?.overall === 100);
  const label = !currentOwner ? "Assign a responsible party to sign"
    : !allGreen ? "Complete checklist to sign"
    : !scOk ? "Scorecard must be 100%"
    : "Sign gate & advance";
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold tracking-tight">Gate: {stage.name}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Exit criteria enforced. Accountable owner:{" "}
        {currentOwner
          ? <span className="text-white">{currentOwner.name} ({DESIGNATION_LABELS[currentOwner.designation] || currentOwner.designation})</span>
          : <span className="text-danger">Unassigned — assign above</span>}
      </p>

      <div className="space-y-2">
        {(gate.checklist || []).map((c) => (
          <label key={c.id} data-testid={`checklist-${c.id}`} className="flex items-start gap-2.5 p-2 rounded-sm hover:bg-white/[0.04] cursor-pointer transition-colors duration-200">
            <Checkbox checked={c.checked} disabled={busy} onCheckedChange={(v) => toggleItem(c.id, !!v)} className="mt-0.5" data-testid={`checkbox-${c.id}`} />
            <span className={`text-sm ${c.checked ? "text-white" : "text-muted-foreground"}`}>{c.label}</span>
          </label>
        ))}
      </div>

      {gate.signed_off && (
        <div className="mt-3 flex items-center gap-2 text-xs text-success"><CheckCircle2 className="w-4 h-4" /> Signed by {gate.signed_by}</div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <Button data-testid="signoff-btn" onClick={signGate} disabled={!canSign || busy} className="w-full rounded-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          {label}
        </Button>
      </div>
    </div>
  );
}

function CouncilPanel({ idea, currentOwner, act, ideaId, busy }) {
  const brief = idea.ai?.council_brief;
  const [reason, setReason] = useState("");

  const decide = (decision) => {
    if (!reason.trim()) { toast.error("A reason is required for the decision log"); return; }
    act(() => api.post(`/ideas/${ideaId}/decision`, { decision, reason }), `Decision logged: ${decision.toUpperCase()}`);
  };

  return (
    <div className="space-y-4">
      {brief && (
        <div className="border border-ai/30 bg-card rounded-sm p-4 ai-glow">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-ai" />
            <h3 className="font-heading font-bold tracking-tight">AI Council Brief</h3>
            <span className="ml-auto font-mono text-xs text-ai">Recommends: {brief.recommendation} ({brief.confidence}%)</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <BriefBlock label="Value" text={brief.value} />
            <BriefBlock label="Scalability" text={brief.scalability} />
            <BriefBlock label="Differentiation" text={brief.differentiation} />
            <BriefBlock label="Risk" text={brief.risk} />
          </div>
          {brief.evidence?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Evidence</p>
              <ul className="space-y-1">{brief.evidence.map((e, i) => <li key={i} className="text-xs text-muted-foreground">• {e}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="border border-warning/30 bg-card rounded-sm p-4">
        <h3 className="font-heading font-bold tracking-tight mb-1">High-Stakes Decision</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Human decision required. Accountable:{" "}
          {currentOwner
            ? <span className="text-white">{currentOwner.name} ({DESIGNATION_LABELS[currentOwner.designation] || currentOwner.designation})</span>
            : <span className="text-danger">no AI Council owner assigned</span>}
        </p>
        {!currentOwner ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Assign an AI Council responsible party above before deciding.</p>
        ) : (
          <>
            <Textarea data-testid="decision-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reasoning for the decision (required)…" rows={3} className="rounded-sm bg-background resize-none mb-3" />
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid="decide-approve-btn" onClick={() => decide("approve")} disabled={busy} className="rounded-sm bg-success hover:bg-success/90 text-black font-semibold"><ThumbsUp className="w-4 h-4 mr-1.5" />Approve</Button>
              <Button data-testid="decide-iterate-btn" onClick={() => decide("iterate")} disabled={busy} variant="outline" className="rounded-sm border-warning/50 text-warning hover:bg-warning/10"><RotateCcw className="w-4 h-4 mr-1.5" />Iterate</Button>
              <Button data-testid="decide-reject-btn" onClick={() => decide("reject")} disabled={busy} variant="outline" className="rounded-sm border-danger/50 text-danger hover:bg-danger/10"><XCircle className="w-4 h-4 mr-1.5" />Reject</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AdoptionPanel({ idea, act, ideaId, busy, currentOwner, canSign, allGreen, signGate, gate, toggleItem }) {
  const [note, setNote] = useState("");
  const addFeedback = () => {
    if (!note.trim()) return;
    act(() => api.post(`/ideas/${ideaId}/feedback`, { note }), "Feedback captured & synthesized to backlog");
    setNote("");
  };
  return (
    <div className="space-y-4">
      <div className="border border-border bg-card rounded-sm p-4">
        <h3 className="font-heading font-bold tracking-tight mb-1 flex items-center gap-2"><MessageSquarePlus className="w-4 h-4 text-primary" />Client Feedback Loop</h3>
        <p className="text-xs text-muted-foreground mb-3">AI synthesizes raw client feedback into prioritized product backlog items.</p>
        <div className="flex gap-2">
          <Textarea data-testid="feedback-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Paste a client note…" rows={2} className="rounded-sm bg-background resize-none" />
          <Button data-testid="add-feedback-btn" onClick={addFeedback} disabled={busy} className="rounded-sm">Add</Button>
        </div>
        {idea.backlog?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">AI-generated backlog</p>
            {idea.backlog.map((b) => (
              <div key={b.id} className="flex items-center justify-between border border-border rounded-sm p-2">
                <span className="text-sm">{b.title}</span>
                <span className="font-mono text-[10px] text-ai">{b.priority}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <GatePanel gate={gate} stage={STAGE_MAP.adoption_scale} currentOwner={currentOwner} canSign={canSign} allGreen={allGreen} busy={busy} signGate={signGate} toggleItem={toggleItem} idea={idea} />
    </div>
  );
}

function HistoryPanel({ idea }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <h3 className="font-heading font-bold tracking-tight mb-3">Audit Trail</h3>
      <div className="space-y-3">
        {[...(idea.history || [])].reverse().map((h, i) => (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className="w-2 h-2 rounded-full bg-primary mt-1.5" />
              {i < idea.history.length - 1 && <span className="w-px flex-1 bg-border" />}
            </div>
            <div className="pb-1">
              <p className="text-sm">{h.event}</p>
              <p className="text-[10px] font-mono text-muted-foreground">{h.by} · {new Date(h.at).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssignmentBar({ team, currentOwner, isDefault, onAssign, busy, stage }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <UserCheck className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold">Responsible party</span>
        <span className="font-mono text-[10px] text-muted-foreground">· {stage.short}</span>
      </div>
      <div className="flex items-center gap-2 flex-1">
        {currentOwner && (
          <Avatar className="w-7 h-7 rounded-sm">
            <AvatarImage src={currentOwner.avatar_url} className="object-cover" />
            <AvatarFallback className="rounded-sm bg-primary text-black text-[10px] font-bold">{currentOwner.name.split(" ").map((n) => n[0]).join("")}</AvatarFallback>
          </Avatar>
        )}
        <Select value={currentOwner?.id || "none"} onValueChange={(v) => onAssign(v === "none" ? null : v)} disabled={busy}>
          <SelectTrigger data-testid="assign-owner-select" className="rounded-sm bg-background h-9 max-w-xs"><SelectValue placeholder="Assign a responsible party" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {DESIGNATION_LABELS[m.designation] || m.designation}</SelectItem>)}
          </SelectContent>
        </Select>
        {currentOwner && isDefault && <span className="font-mono text-[10px] text-muted-foreground">default</span>}
      </div>
    </div>
  );
}

function DemoReadinessPanel({ idea, onToggle, busy }) {
  const dr = idea.demo_readiness || { checklist: [] };
  const pct = dr.pct ?? 0;
  const ready = dr.ready;
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Rocket className={`w-4 h-4 ${ready ? "text-success" : "text-primary"}`} />
        <h3 className="font-heading font-bold tracking-tight">Client Demo Readiness</h3>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <span className={`font-mono text-2xl font-bold ${ready ? "text-success" : "text-warning"}`}>{pct}%</span>
        <Progress value={pct} className="h-2 flex-1" />
      </div>
      <div className="space-y-1.5">
        {dr.checklist.map((c) => (
          <label key={c.id} data-testid={`readiness-${c.id}`} className="flex items-start gap-2.5 p-1.5 rounded-sm hover:bg-white/[0.04] cursor-pointer transition-colors duration-200">
            <Checkbox checked={c.checked} disabled={busy} onCheckedChange={(v) => onToggle(c.id, !!v)} className="mt-0.5" data-testid={`readiness-check-${c.id}`} />
            <span className={`text-sm ${c.checked ? "text-white" : "text-muted-foreground"}`}>{c.label}</span>
          </label>
        ))}
      </div>
      {ready && <div className="mt-3 flex items-center gap-2 text-xs text-success"><CheckCircle2 className="w-4 h-4" /> Ready to demo to the client.</div>}
    </div>
  );
}

function AutoGateNote({ gate, stage }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-ai" />
        <h3 className="font-heading font-bold tracking-tight">Gate: {stage.name}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">AI-owned auto-gate. The agent scores and decides automatically — no human sign-off required.</p>
      <div className="space-y-2">
        {(gate.checklist || []).map((c) => (
          <div key={c.id} className="flex items-start gap-2.5 p-2">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 ${c.checked ? "text-success" : "text-muted-foreground"}`} />
            <span className={`text-sm ${c.checked ? "text-white" : "text-muted-foreground"}`}>{c.label}</span>
          </div>
        ))}
      </div>
      {gate.signed_off && (
        <div className="mt-3 flex items-center gap-2 text-xs text-success"><CheckCircle2 className="w-4 h-4" /> Auto-signed by {gate.signed_by}</div>
      )}
    </div>
  );
}

function Metric({ label, value, suffix = "", small }) {
  return (
    <div className={small ? "border border-border rounded-sm p-2 text-center" : ""}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-mono font-bold ${small ? "text-xl" : "text-3xl"} ${value >= 70 ? "text-success" : value >= 50 ? "text-warning" : "text-danger"}`}>{value}{suffix}</p>
    </div>
  );
}

function BriefBlock({ label, text }) {
  return (
    <div className="border border-border rounded-sm p-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-ai mb-1">{label}</p>
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
